import { Link } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import {
  Avatar, Body, Button, Card, Eyebrow, Heading, I, Icon, Row, Stack, TONE, type Tone, } from '@leanin/ui';
import type { AttachedEntityView } from '@leanin/shared';

import { RelativeDate } from '../dates/RelativeDate';

const COVER_WIDTH = 96;

type EntityKind = AttachedEntityView['kind'];

export function toneFor(kind: EntityKind): Tone {
  switch (kind) {
    case 'resource':
      return 'yellow';
    case 'event':
      return 'orange';
    case 'circle':
      return 'burgundy';
    case 'network':
      return 'navy';
    case 'profile':
      return 'emerald';
    case 'post':
      return 'navy';
  }
}

function iconFor(kind: EntityKind): React.ReactNode {
  switch (kind) {
    case 'resource':
      return I.book;
    case 'event':
      return I.cal;
    case 'circle':
      return I.circles;
    case 'network':
      return I.network;
    case 'profile':
      return I.users;
    case 'post':
      return I.chat;
  }
}

type Props = {
  entity: AttachedEntityView;
  /** Render as a link to the entity's href. Default true. */
  interactive?: boolean;
};

export function PostEntityCard({ entity, interactive = true }: Props) {
  const inner = <PostEntityCardBody entity={entity} />;
  if (!interactive) {
    return (
      <Card pad="flush" inset style={{ overflow: 'hidden', height: '100%' }}>
        {inner}
      </Card>
    );
  }
  return (
    <Card pad="flush" inset style={{ overflow: 'hidden', height: '100%' }}>
      <Link
        to={entity.href}
        style={{
          color: 'inherit',
          textDecoration: 'none',
          display: 'block',
          height: '100%',
        }}
      >
        {inner}
      </Link>
    </Card>
  );
}

function PostEntityCardBody({
  entity,
  trailing,
}: {
  entity: AttachedEntityView;
  trailing?: React.ReactNode;
}) {
  const tone = toneFor(entity.kind);
  const t = useTranslations('Attached');
  const eyebrow = t(`eyebrow.${entity.kind}`);

  // Cover-image-bearing kinds use the cover; others render a tone-coded
  // glyph block. Spec: cover infra exists only for resources in v1; circles,
  // networks, events, profiles, posts render text-only / glyph-only.
  const coverUrl = pickCoverUrl(entity);

  const palette = TONE[tone];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `${COVER_WIDTH}px 1fr`,
        height: '100%',
      }}
    >
      <div
        style={{
          background:
            coverUrl !== null ? `url(${coverUrl}) center/cover` : palette.bg,
          color: palette.ink,
          // Inner display:grid + place-items:center centers the Icon. The
          // outer `display: grid` on the card body sizes this cell; the
          // inner grid is just a centering mechanism.
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {coverUrl === null ? <Icon size="lg">{iconFor(entity.kind)}</Icon> : null}
      </div>
      <div
        style={{
          // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- raw <div> entity card body cell with border + flex content; no Box prop
          padding: 'var(--space-lg)',
          borderLeft: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- raw <div> entity card body cell; no Box prop
          gap: 'var(--space-sm)',
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- raw <div> stacks meta over title; no Box prop
            gap: 'var(--space-xs)',
            minWidth: 0,
            flex: 1,
          }}
        >
          <Eyebrow>{eyebrow}</Eyebrow>
          <Heading
            level={5}
            style={{
              // 2-line clamp with ellipsis. Cards stay compact in a row;
              // long titles get truncated rather than blowing out the row.
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {titleFor(entity)}
          </Heading>
          <PostEntityMeta entity={entity} />
        </div>
        {trailing}
      </div>
    </div>
  );
}

function pickCoverUrl(entity: AttachedEntityView): string | null {
  if (entity.kind === 'resource') return entity.coverImageUrl;
  if (entity.kind === 'post') return entity.mediaThumbUrl;
  if (entity.kind === 'profile') return entity.photoUrl;
  return null;
}

function titleFor(entity: AttachedEntityView): string {
  switch (entity.kind) {
    case 'resource':
    case 'event':
    case 'circle':
    case 'network':
      return entity.title;
    case 'profile':
      return entity.displayName;
    case 'post':
      return entity.authorDisplayName;
  }
}

// All meta lines truncate to a single line with ellipsis — keeps card
// height predictable across kinds and prevents long headlines /
// excerpts from breaking row symmetry.
const META_TRUNCATE_STYLE = {
  whiteSpace: 'nowrap' as const,
  overflow: 'hidden' as const,
  textOverflow: 'ellipsis' as const,
};

function PostEntityMeta({ entity }: { entity: AttachedEntityView }) {
  const t = useTranslations('Attached');
  switch (entity.kind) {
    case 'resource':
      return (
        <Body size="sm" color="mute" style={META_TRUNCATE_STYLE}>
          {entity.pageCount !== undefined
            ? `${t('meta.resourcePages', { count: entity.pageCount })} · `
            : ''}
          {t.rich('meta.updatedRelative', {
            when: () => <RelativeDate value={entity.updatedAt} />,
          })}
        </Body>
      );
    case 'event':
      return (
        <Body size="sm" color="mute" style={META_TRUNCATE_STYLE}>
          <RelativeDate value={entity.startsAt} />
        </Body>
      );
    case 'circle':
    case 'network':
      return (
        <Body size="sm" color="mute" style={META_TRUNCATE_STYLE}>
          {t(
            entity.kind === 'circle'
              ? 'meta.circleMembers'
              : 'meta.networkMembers',
            { count: entity.memberCount },
          )}
        </Body>
      );
    case 'profile':
      return entity.headline !== null ? (
        <Body size="sm" color="mute" style={META_TRUNCATE_STYLE}>
          {entity.headline}
        </Body>
      ) : null;
    case 'post':
      return (
        <Body size="sm" color="mute" style={META_TRUNCATE_STYLE}>
          {entity.bodyExcerpt}
        </Body>
      );
  }
}

// ---------------------------------------------------------------------------
// Editable variant — composer affordances (swap / remove)
// ---------------------------------------------------------------------------

type EditableProps = {
  entity: AttachedEntityView;
  onSwap?: () => void;
  onRemove?: () => void;
  /** Optional inline error pill text (e.g. "not visible to you"). */
  errorMessage?: string | null;
};

function PostEntityCardEditable({
  entity,
  onSwap,
  onRemove,
  errorMessage = null,
}: EditableProps) {
  const t = useTranslations('Attached');
  // Inline-trailing controls, matching PostMediaDocumentCard.Editable.
  // No footer row — composer-attachment cards across kinds use a single
  // body row with editing chrome on the trailing edge.
  const trailing = (
    <Row gap="xs" align="center">
      {onSwap !== undefined ? (
        <Button variant="ghost" size="sm" onClick={onSwap}>
          {t('labels.swap')}
        </Button>
      ) : null}
      {onRemove !== undefined ? (
        <Button
          variant="ghost"
          size="sm"
          icon={I.x}
          aria-label={t('labels.remove')}
          onClick={onRemove}
        />
      ) : null}
    </Row>
  );
  return (
    <Stack gap="sm">
      <Card pad="flush" inset style={{ overflow: 'hidden' }}>
        <PostEntityCardBody entity={entity} trailing={trailing} />
      </Card>
      {errorMessage !== null ? (
        <Body color="destructive">
          {errorMessage}
        </Body>
      ) : null}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// "Author" mini-card variant — used on /profile/[id] etc. when rendering an
// AttachedEntityView profile card without the full Card frame. Avatar lives
// off the entity's photoUrl.
// ---------------------------------------------------------------------------

export function PostEntityAvatar({ entity }: { entity: AttachedEntityView }) {
  if (entity.kind !== 'profile') return null;
  return (
    <Avatar
      src={entity.photoUrl ?? undefined}
      name={entity.displayName}
      size={44}
    />
  );
}

PostEntityCard.Editable = PostEntityCardEditable;
PostEntityCard.toneFor = toneFor;
